import { useContext, useEffect, useState } from 'react'

import APIS from './APIS'
import UserContext from '../contexts/UserContext'

/* ⛔ ACCES A « MA CERTIFICATION » (06/10/2026, regle de Nawal).

   L'espace n'est ouvert que si l'etablissement a souscrit la certification.
   Il n'existe PAS de statut « certification oui/non » : on le DEDUIT des
   valeurs saisies dans « Gestion de la certification » (admin, fiche du
   groupe, `CertificationModal.js`), enregistrees dans `groupe.certification`
   et renvoyees a l'apprenant par `getCertifInfos` (`result.certification`).

   Ce tableau contient deux sortes d'entrees :
     - `{ id: "valuesForNiveauConnaissance", before, after }` : les seuils du
       NIVEAU DE CONNAISSANCE, avant et apres la formation ;
     - `{ id: <id du module>, softSkills: [{ key, validation }] }` : le seuil
       de validation de chaque compétence, module par module.

   REGLE : la certification est ACTIVEE des qu'UNE de ces valeurs est un
   nombre strictement positif. Toutes a 0, ou aucune renseignee (tableau vide,
   entrees sans valeur, champ absent) : l'etablissement n'a pas souscrit.
   `seuil` est lu a defaut de `validation` : meme repli que l'espace client
   (`AdminDashboard/Certification.js`, `seuilsDuGroupe`), deja rencontre sur
   des donnees anciennes.

   ⚠️ C'EST UN VERROU D'INTERFACE, PAS UN DROIT. Le serveur repond a
   `getCertifInfos` sans regarder cette regle (non verifie : le back n'est pas
   sur ce poste). Point consigne pour Hicham. */

const positif = (v) => {
  if (v === null || v === undefined || v === '') return false
  const n = Number(v)
  return Number.isFinite(n) && n > 0
}

export const certificationActivee = (certification) => {
  let liste = certification
  if (typeof liste === 'string') {
    try { liste = JSON.parse(liste) } catch (e) { return false }
  }
  if (!Array.isArray(liste)) return false
  return liste.some((entree) => {
    if (!entree || typeof entree !== 'object') return false
    if (entree.id === 'valuesForNiveauConnaissance') {
      return positif(entree.before) || positif(entree.after)
    }
    return (Array.isArray(entree.softSkills) ? entree.softSkills : []).some((sk) =>
      positif(sk?.validation !== undefined ? sk.validation : sk?.seuil))
  })
}

/* Statuts : 'chargement' tant qu'on ne sait pas ; 'active' ; 'inactive' ;
   'inconnu' si la reponse n'est pas exploitable (panne reseau, erreur
   serveur). 'inconnu' laisse l'onglet OUVERT : annoncer a un apprenant dont
   l'ecole a souscrit que l'espace « n'est pas inclus dans son parcours »,
   parce que le reseau a hoquete, serait faux et inquietant. La page garde
   alors son comportement d'avant. */
export const statutDepuisReponse = (reponse) => {
  if (reponse?.message !== 'getCertifInfos') return 'inconnu'
  return certificationActivee(reponse?.certification) ? 'active' : 'inactive'
}

/* UNE requete par apprenant et par chargement de page, partagee par le menu,
   le tableau de bord et la page : le menu disparait sur l'ecran d'un module
   et revient au tableau de bord, il ne doit pas recharger (ni se cacher) a
   chaque retour. La page « Ma certification », qui relit l'API a chaque
   visite, met ce statut a jour (`noterAccesCertification`). */
/* 06/10/2026 : on garde aussi les VALEURS (le tableau `certification`),
   pour que l'ecran de fin de module affiche le seuil de chaque competence
   sans relire l'API (`useCertificationDuGroupe`, plus bas). */
let memoire = { cle: null, statut: null, promesse: null, certification: null }
const abonnes = new Set()
const publier = () => abonnes.forEach((f) => f(memoire.cle, memoire.statut))

const enTableau = (certification) => {
  let liste = certification
  if (typeof liste === 'string') { try { liste = JSON.parse(liste) } catch (e) { liste = null } }
  return Array.isArray(liste) ? liste : null
}

export const noterAccesCertification = (cle, statut, certification) => {
  if (cle === null || cle === undefined) return
  if (memoire.cle !== cle) memoire = { cle, statut: null, promesse: null, certification: null }
  memoire.statut = statut
  if (certification !== undefined) memoire.certification = enTableau(certification)
  publier()
}

const charger = (cle) => {
  if (memoire.cle === cle && memoire.promesse) return memoire.promesse
  memoire = { cle, statut: null, promesse: null, certification: null }
  memoire.promesse = Promise.resolve()
    .then(() => APIS.getCertifInfos())
    .then((reponse) => ({ statut: statutDepuisReponse(reponse), certification: reponse?.message === 'getCertifInfos' ? enTableau(reponse?.certification) : null }),
          () => ({ statut: 'inconnu', certification: null }))
    .then(({ statut, certification }) => {
      if (memoire.cle === cle && memoire.statut === null) {
        memoire.statut = statut
        memoire.certification = certification
        publier()
      }
      return statut
    })
  return memoire.promesse
}

/* Le statut deja connu pour cet apprenant, ou null. */
export const accesCertificationMemorise = (cle) =>
  (cle !== null && cle !== undefined && memoire.cle === cle) ? memoire.statut : null

/* Pour les tests, et pour une deconnexion sans rechargement de page. */
export const oublierAccesCertification = () => {
  memoire = { cle: null, statut: null, promesse: null, certification: null }
}

/* Au-dela, on cesse d'attendre et l'onglet s'affiche ouvert ('inconnu') :
   une requete pendue ne doit pas laisser le menu ampute. */
export const DELAI_ACCES_CERTIFICATION_MS = 8000

export const useAccesCertification = (actif = true) => {
  const { currentUser } = useContext(UserContext) || {}
  const cle = actif && currentUser?.id !== undefined && currentUser?.id !== null ? currentUser.id : null
  const [etat, setEtat] = useState(() =>
    (cle !== null && memoire.cle === cle && memoire.statut) ? memoire.statut : 'chargement')

  useEffect(() => {
    if (cle === null) return undefined
    let vivant = true
    const ecouter = (c, s) => { if (vivant && c === cle && s) setEtat(s) }
    abonnes.add(ecouter)
    if (memoire.cle === cle && memoire.statut) {
      setEtat(memoire.statut)
    } else {
      setEtat('chargement')
      charger(cle)
    }
    const minuterie = setTimeout(() => {
      if (vivant) setEtat((s) => (s === 'chargement' ? 'inconnu' : s))
    }, DELAI_ACCES_CERTIFICATION_MS)
    return () => { vivant = false; abonnes.delete(ecouter); clearTimeout(minuterie) }
  }, [cle])

  return cle === null ? 'chargement' : etat
}

/* ⛔ 06/10/2026 (Nawal) : LE SEUIL D'UNE COMPETENCE, POUR UN MODULE DONNE.
   La valeur saisie dans « Gestion de la certification » du groupe
   (`softSkills[].validation` de l'entree du module, `seuil` a defaut), et
   SEULEMENT si la certification du groupe est activee (meme regle que
   l'onglet). Rien si elle ne l'est pas, si la valeur manque, est vide ou
   vaut 0 : on n'affiche jamais un seuil de certification qui n'existe pas. */
export const seuilDeCertification = ({ statut, certification }, idModule, cleCompetence) => {
  if (statut !== 'active' || !Array.isArray(certification)) return undefined
  const entree = certification.find((e) => e && e.id !== 'valuesForNiveauConnaissance' && String(e.id) === String(idModule))
  const sk = (Array.isArray(entree?.softSkills) ? entree.softSkills : []).find((x) => x?.key === cleCompetence)
  const brut = sk?.validation !== undefined ? sk.validation : sk?.seuil
  if (brut === null || brut === undefined || brut === '') return undefined
  const n = Number(brut)
  return Number.isFinite(n) && n > 0 ? n : undefined
}

/* Le statut ET les valeurs, pour l'ecran de fin de module. */
export const useCertificationDuGroupe = (actif = true) => {
  const statut = useAccesCertification(actif)
  return { statut, certification: statut === 'active' ? memoire.certification : null }
}
